import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { 
  Plus, Search, Filter, Cog, Clock, 
  Euro, BarChart3, Eye, Edit, MoreVertical
} from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Select from '../components/ui/Select'
import Modal from '../components/Modal'
import Pagination from '../components/ui/Pagination'
import Badge from '../components/ui/Badge'

import servicosService, { 
  ServicoFilters,
  formatarPreco,
  getCategoriaLabel,
  getStatusServico
} from '../services/servicos'
import { Servico } from '../types'
import ServicoForm from '../components/servicos/ServicoForm'
import ServicoDetails from '../components/servicos/ServicoDetails'

const ServicosPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()

  // Estados locais
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedServico, setSelectedServico] = useState<Servico | null>(null)
  const [showFilters, setShowFilters] = useState(false)

  // Filtros
  const [filters, setFilters] = useState<ServicoFilters>({
    page: parseInt(searchParams.get('page') || '1'),
    limit: parseInt(searchParams.get('limit') || '25'),
    search: searchParams.get('search') || '',
    categoria: searchParams.get('categoria') || undefined,
    ativo: searchParams.get('ativo') ? searchParams.get('ativo') === 'true' : undefined
  })

  // Queries
  const { data: servicosData, isLoading } = useQuery({
    queryKey: ['servicos', filters],
    queryFn: () => servicosService.list(filters)
  })

  const { data: categorias } = useQuery({
    queryKey: ['servicos-categorias'],
    queryFn: () => servicosService.getCategorias()
  })

  const { data: stats } = useQuery({
    queryKey: ['servicos-stats'],
    queryFn: () => servicosService.getStats()
  })

  // Efeitos
  useEffect(() => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        params.set(key, String(value))
      }
    })
    setSearchParams(params)
  }, [filters, setSearchParams])

  // Handlers
  const handleSearch = (search: string) => {
    setFilters(prev => ({ ...prev, search, page: 1 }))
  }

  const handleFilterChange = (key: keyof ServicoFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }))
  }

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }

  const handleCreate = () => {
    setShowCreateModal(true)
  }

  const handleEdit = (servico: Servico) => {
    setSelectedServico(servico)
    setShowEditModal(true)
  }

  const handleView = (servico: Servico) => {
    setSelectedServico(servico)
    setShowDetailsModal(true)
  }

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['servicos'] })
    queryClient.invalidateQueries({ queryKey: ['servicos-stats'] })
    setShowCreateModal(false)
    setShowEditModal(false)
    setSelectedServico(null)
  }

  const handleCloseModals = () => {
    setShowCreateModal(false)
    setShowEditModal(false)
    setShowDetailsModal(false)
    setSelectedServico(null)
  }

  // Opções para filtros
  const categoriaOptions = categorias?.map(cat => ({
    value: cat,
    label: getCategoriaLabel(cat)
  })) || []

  const statusOptions = [
    { value: '', label: 'Todos' },
    { value: 'true', label: 'Ativos' },
    { value: 'false', label: 'Inativos' }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Serviços</h1>
          <p className="text-gray-600">
            Gerencie os serviços oferecidos pela empresa
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Serviço
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Cog className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total de Serviços</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <BarChart3 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Serviços Ativos</p>
                <p className="text-2xl font-bold text-gray-900">{stats.ativos}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Categorias</p>
                <p className="text-2xl font-bold text-gray-900">{stats.porCategoria.length}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Pesquisar serviços..."
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Filter Button */}
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="lg:w-auto"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtros
          </Button>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select
                label="Categoria"
                value={filters.categoria || ''}
                onChange={(e) => handleFilterChange('categoria', e.target.value || undefined)}
                options={[
                  { value: '', label: 'Todas as categorias' },
                  ...categoriaOptions
                ]}
              />

              <Select
                label="Status"
                value={filters.ativo !== undefined ? String(filters.ativo) : ''}
                onChange={(e) => handleFilterChange('ativo', e.target.value === '' ? undefined : e.target.value === 'true')}
                options={statusOptions}
              />

              <Select
                label="Itens por página"
                value={String(filters.limit)}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                options={[
                  { value: '10', label: '10 itens' },
                  { value: '25', label: '25 itens' },
                  { value: '50', label: '50 itens' },
                  { value: '100', label: '100 itens' }
                ]}
              />
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Serviço
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Categoria
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preço
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tempo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    Carregando serviços...
                  </td>
                </tr>
              ) : servicosData?.servicos.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    Nenhum serviço encontrado
                  </td>
                </tr>
              ) : (
                servicosData?.servicos.map((servico) => {
                  const status = getStatusServico(servico.ativo)
                  
                  return (
                    <tr key={servico.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {servico.nome}
                          </div>
                          {servico.descricao && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {servico.descricao}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant="outline">
                          {getCategoriaLabel(servico.categoria)}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatarPreco(servico.preco)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <Clock className="w-4 h-4" />
                          <span>{servico.tempoEstimado}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge color={status.color} variant="dot">
                          {status.label}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleView(servico)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(servico)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {servicosData && servicosData.pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <Pagination
              currentPage={servicosData.pagination.currentPage}
              totalPages={servicosData.pagination.totalPages}
              onPageChange={handlePageChange}
              totalItems={servicosData.pagination.totalItems}
              itemsPerPage={servicosData.pagination.itemsPerPage}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      <Modal
        isOpen={showCreateModal}
        onClose={handleCloseModals}
        title="Novo Serviço"
        size="lg"
      >
        <ServicoForm
          onSuccess={handleSuccess}
          onCancel={handleCloseModals}
        />
      </Modal>

      <Modal
        isOpen={showEditModal}
        onClose={handleCloseModals}
        title="Editar Serviço"
        size="lg"
      >
        {selectedServico && (
          <ServicoForm
            servico={selectedServico}
            onSuccess={handleSuccess}
            onCancel={handleCloseModals}
          />
        )}
      </Modal>

      <Modal
        isOpen={showDetailsModal}
        onClose={handleCloseModals}
        title="Detalhes do Serviço"
        size="lg"
      >
        {selectedServico && (
          <ServicoDetails
            servico={selectedServico}
            onEdit={() => {
              setShowDetailsModal(false)
              setShowEditModal(true)
            }}
            onClose={handleCloseModals}
          />
        )}
      </Modal>
    </div>
  )
}

export default ServicosPage